import { delayExceedsLimit } from '@/lib/delay-limits/verdict';
import { newIdempotencyKey } from '@/lib/uuid';
import { localToUtcIso } from './time';
import type {
  DelayDeclarationKind,
  DelayDeclarationReason,
  IDelayDeclarationBody,
} from './types';

/**
 * The manual declaration (U1) as a pure model: the form's draft, its inline validation,
 * the wire body, and the Idempotency-Key discipline. Nothing here renders or holds
 * language — errors are i18n KEYS of the `disruptions` namespace.
 *
 * The server rules this mirrors (`flight-delay.tds.md` §8.4) — checked here so the
 * operator sees them next to the input before anything is sent, and re-checked there:
 *   - DELAY needs an ETD, strictly after the STD;
 *   - an STD must be known (declared, the timeline's, or the flight's own);
 *   - a declared ATD may not lie in the future (5 min of clock skew tolerated);
 *   - note ≤ 500 characters;
 *   - a DELAY may not exceed the policy maximum (`overLimitMinutes`, delay-limits) — not a
 *     field error: the form says it once, with «Declarar cancelado» as the way out.
 */

/** The form's state. Instants are `datetime-local` strings in the departure airport's zone. */
export interface IDelayDraft {
  kind: DelayDeclarationKind;
  std: string;
  etd: string;
  atd: string;
  reason: DelayDeclarationReason | '';
  note: string;
}

export interface IDraftContext {
  flightUrn: string;
  /** IANA zone the draft's wall times are read in (`time.ts` `zoneFor`). */
  zone: string;
  /** `Date.now()` — injected so validation is deterministic in tests. */
  now: number;
  /** The STD may be left blank only when the server knows one already (an open timeline). */
  stdKnown: boolean;
}

export type DraftField = 'std' | 'etd' | 'atd' | 'note';
export type DraftErrors = Partial<Record<DraftField, string>>;

export const NOTE_MAX = 500;
const ATD_FUTURE_SKEW_MS = 5 * 60_000;

export function emptyDraft(kind: DelayDeclarationKind = 'DELAY', std = ''): IDelayDraft {
  return { kind, std, etd: '', atd: '', reason: '', note: '' };
}

export function validateDraft(draft: IDelayDraft, ctx: IDraftContext): DraftErrors {
  const errors: DraftErrors = {};
  const std = draft.std ? localToUtcIso(draft.std, ctx.zone) : null;
  if (draft.std && !std) errors.std = 'delay.form.errors.invalidTime';
  else if (!draft.std && !ctx.stdKnown) errors.std = 'delay.form.errors.stdRequired';

  if (draft.kind === 'DELAY') {
    const etd = draft.etd ? localToUtcIso(draft.etd, ctx.zone) : null;
    if (!draft.etd) errors.etd = 'delay.form.errors.etdRequired';
    else if (!etd) errors.etd = 'delay.form.errors.invalidTime';
    else if (std && Date.parse(etd) <= Date.parse(std)) errors.etd = 'delay.form.errors.etdNotAfterStd';
  }

  if (draft.kind === 'DEPARTED' && draft.atd) {
    const atd = localToUtcIso(draft.atd, ctx.zone);
    if (!atd) errors.atd = 'delay.form.errors.invalidTime';
    else if (Date.parse(atd) > ctx.now + ATD_FUTURE_SKEW_MS) errors.atd = 'delay.form.errors.atdInFuture';
  }

  if (draft.note.trim().length > NOTE_MAX) errors.note = 'delay.form.errors.noteTooLong';
  return errors;
}

/**
 * The policy maximum (minutes) a DELAY draft's ETD exceeds — `ETD − STD > limit`, strict,
 * the predicate every layer uses (`delayExceedsLimit`) —, or `null`: another kind, an
 * incomplete STD / ETD, or no limit known yet (cases then decides, `delay_exceeds_limit`).
 */
export function overLimitMinutes(draft: IDelayDraft, zone: string, limitMinutes: number | null): number | null {
  if (draft.kind !== 'DELAY' || !limitMinutes) return null;
  const std = draft.std ? localToUtcIso(draft.std, zone) : null;
  const etd = draft.etd ? localToUtcIso(draft.etd, zone) : null;
  return std && etd && delayExceedsLimit(std, etd, limitMinutes / 60).exceeds ? limitMinutes : null;
}

export function hasErrors(errors: DraftErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * The wire body of a VALID draft (call `validateDraft` first). Only the fields the kind
 * uses are sent: an ETD rides only on a DELAY, an ATD only on a DEPARTED, empty fields
 * are omitted (the server then falls back to the timeline / the flight).
 */
export function toDeclarationBody(draft: IDelayDraft, ctx: Pick<IDraftContext, 'flightUrn' | 'zone'>): IDelayDeclarationBody {
  const std = draft.std ? localToUtcIso(draft.std, ctx.zone) : null;
  const etd = draft.kind === 'DELAY' && draft.etd ? localToUtcIso(draft.etd, ctx.zone) : null;
  const atd = draft.kind === 'DEPARTED' && draft.atd ? localToUtcIso(draft.atd, ctx.zone) : null;
  const note = draft.note.trim();
  return {
    flightUrn: ctx.flightUrn,
    kind: draft.kind,
    ...(std ? { scheduledDepartureAt: std } : {}),
    ...(etd ? { estimatedDepartureAt: etd } : {}),
    ...(atd ? { actualDepartureAt: atd } : {}),
    ...(draft.reason ? { reason: draft.reason } : {}),
    ...(note ? { note } : {}),
  };
}

/** A stable fingerprint of a body — its fields in a FIXED order (key order never matters). */
export function fingerprintOf(body: IDelayDeclarationBody): string {
  return JSON.stringify([
    body.flightUrn,
    body.kind,
    body.scheduledDepartureAt ?? null,
    body.estimatedDepartureAt ?? null,
    body.actualDepartureAt ?? null,
    body.reason ?? null,
    body.note ?? null,
  ]);
}

export interface ISubmissionKeys {
  /** The key for submitting `body`: the pending submission's key while the body is
   *  unchanged (a RETRY), a fresh one for a different body (a NEW submission). */
  keyFor(body: IDelayDeclarationBody): string;
  /** The submission landed — whatever is sent next is a new submission. */
  settle(): void;
}

/**
 * One Idempotency-Key per user submission (brief §6 invariant 4):
 *   - the SAME key across retries of that submission (a timeout, a 5xx, a dropped
 *     connection): cases replays the stored answer instead of declaring twice;
 *   - a NEW key once it landed (`settle`), or when the operator changed the body —
 *     reusing a key with another body is a 409 `idempotency_key_reused` by contract,
 *     and it IS another declaration.
 * The key is minted lazily, at the first submit — never at render.
 */
export function createSubmissionKeys(mint: () => string = newIdempotencyKey): ISubmissionKeys {
  let pending: { fingerprint: string; key: string } | null = null;
  return {
    keyFor(body) {
      const fingerprint = fingerprintOf(body);
      if (!pending || pending.fingerprint !== fingerprint) pending = { fingerprint, key: mint() };
      return pending.key;
    },
    settle() {
      pending = null;
    },
  };
}

/** The declaration reason a disruption cause implies (the create dialog's default). */
export function reasonForCause(cause: string): DelayDeclarationReason {
  return cause === 'WEATHER' || cause === 'MECHANICAL' || cause === 'STRIKE' ? cause : 'MANUAL';
}
